<template>
  <v-bottom-sheet
    v-model="isOpen"
    :height="sheetHeight"
    scrollable
    :scrim="true"
    class="mobile-discover-sheet"
  >
    <v-card class="mobile-discover-sheet__card">
      <!-- Swipe handle -->
      <div class="mobile-discover-sheet__handle">
        <div class="mobile-discover-sheet__handle-bar" />
      </div>

      <!-- Header -->
      <header class="mobile-discover-sheet__header">
        <div class="mobile-discover-sheet__title">
          <v-icon size="20" color="primary">mdi-compass-outline</v-icon>
          <h2>Discover</h2>
        </div>
        <button class="mobile-discover-sheet__close" @click="close">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </header>

      <v-divider />

      <!-- Content -->
      <div class="mobile-discover-sheet__content">
        <!-- Trending Section -->
        <section class="mobile-discover-sheet__section">
          <header class="mobile-discover-sheet__section-header">
            <v-icon size="18" color="primary">mdi-fire</v-icon>
            <h3>Trending Now</h3>
          </header>

          <!-- Loading state -->
          <div v-if="isLoadingTrending" class="mobile-discover-sheet__loading">
            <v-progress-circular indeterminate size="24" color="primary" />
          </div>

          <div v-else class="mobile-discover-sheet__trends">
            <!-- Trending Hashtags -->
            <div v-if="trendingHashtags.length > 0" class="mobile-discover-sheet__trend-group">
              <span class="mobile-discover-sheet__trend-label">
                <v-icon size="14">mdi-pound</v-icon> Hashtags
              </span>
              <button
                v-for="hashtag in trendingHashtags"
                :key="hashtag.hashtag"
                class="mobile-discover-sheet__trend-item"
                @click="navigateToHashtag(hashtag.hashtag)"
              >
                <div class="mobile-discover-sheet__trend-content">
                  <span class="mobile-discover-sheet__trend-name">{{ hashtag.hashtag }}</span>
                  <span class="mobile-discover-sheet__trend-count">{{ formatCount(hashtag.count) }} posts</span>
                </div>
                <v-icon size="16" class="mobile-discover-sheet__trend-arrow">mdi-chevron-right</v-icon>
              </button>
            </div>

            <!-- Trending Songs -->
            <div v-if="trendingSongs.length > 0" class="mobile-discover-sheet__trend-group">
              <span class="mobile-discover-sheet__trend-label">
                <v-icon size="14">mdi-music</v-icon> Songs
              </span>
              <button
                v-for="song in trendingSongs"
                :key="song.ulid"
                class="mobile-discover-sheet__trend-item mobile-discover-sheet__trend-item--song"
                @click="navigateToSong(song.ulid)"
              >
                <div class="mobile-discover-sheet__trend-cover">
                  <v-img v-if="song.coverImage" :src="song.coverImage" cover />
                  <v-icon v-else size="16">mdi-music-note</v-icon>
                </div>
                <div class="mobile-discover-sheet__trend-content">
                  <span class="mobile-discover-sheet__trend-name">{{ song.title }}</span>
                  <span class="mobile-discover-sheet__trend-count">{{ song.artist }}</span>
                </div>
                <v-icon size="16" class="mobile-discover-sheet__trend-arrow">mdi-play</v-icon>
              </button>
            </div>

            <!-- Trending Creators -->
            <div v-if="trendingCreators.length > 0" class="mobile-discover-sheet__trend-group">
              <span class="mobile-discover-sheet__trend-label">
                <v-icon size="14">mdi-account-music</v-icon> Creators
              </span>
              <button
                v-for="creator in trendingCreators"
                :key="creator.ulid"
                class="mobile-discover-sheet__trend-item mobile-discover-sheet__trend-item--creator"
                @click="navigateToCreator(creator.username)"
              >
                <div class="mobile-discover-sheet__trend-avatar">
                  <v-img v-if="creator.avatar" :src="creator.avatar" cover />
                  <span v-else>{{ creator.username.charAt(0).toUpperCase() }}</span>
                </div>
                <div class="mobile-discover-sheet__trend-content">
                  <span class="mobile-discover-sheet__trend-name">{{ creator.username }}</span>
                  <span class="mobile-discover-sheet__trend-count">{{ formatCount(creator.recentViews) }} plays</span>
                </div>
                <v-icon size="16" class="mobile-discover-sheet__trend-arrow">mdi-chevron-right</v-icon>
              </button>
            </div>

            <!-- Empty state -->
            <div v-if="!hasTrendingContent" class="mobile-discover-sheet__empty">
              <p>No trending content yet</p>
            </div>
          </div>

          <button class="mobile-discover-sheet__more-btn" @click="goToExplore">
            Explore more
            <v-icon size="16">mdi-arrow-right</v-icon>
          </button>
        </section>

        <v-divider class="my-4" />

        <!-- Who to Follow Section -->
        <section v-if="isAuthenticated" class="mobile-discover-sheet__section">
          <header class="mobile-discover-sheet__section-header">
            <v-icon size="18" color="primary">mdi-account-plus-outline</v-icon>
            <h3>Who to Follow</h3>
          </header>

          <!-- Loading state -->
          <div v-if="isLoadingSuggestions" class="mobile-discover-sheet__loading">
            <v-progress-circular indeterminate size="24" color="primary" />
          </div>

          <!-- Empty state -->
          <div v-else-if="suggestedUsers.length === 0" class="mobile-discover-sheet__empty">
            <p>No suggestions available</p>
          </div>

          <!-- Users list -->
          <div v-else class="mobile-discover-sheet__suggestions">
            <div
              v-for="user in suggestedUsers"
              :key="user.ulid"
              class="mobile-discover-sheet__suggestion"
              @click="navigateToProfile(user)"
            >
              <div class="mobile-discover-sheet__suggestion-avatar">
                <v-img v-if="user.avatar" :src="user.avatar" cover />
                <span v-else>{{ user.name.charAt(0).toUpperCase() }}</span>
              </div>
              <div class="mobile-discover-sheet__suggestion-info">
                <span class="mobile-discover-sheet__suggestion-name">{{ user.name }}</span>
                <span class="mobile-discover-sheet__suggestion-handle">@{{ user.username }}</span>
              </div>
              <button
                class="mobile-discover-sheet__follow-btn"
                :class="getFollowButtonClass(user)"
                :disabled="user.isFollowLoading"
                @click.stop="toggleFollow(user)"
              >
                <v-progress-circular
                  v-if="user.isFollowLoading"
                  indeterminate
                  size="14"
                  width="2"
                />
                <template v-else>
                  <v-icon size="14">{{ getFollowIcon(user) }}</v-icon>
                  <span>{{ getFollowButtonText(user) }}</span>
                </template>
              </button>
            </div>
          </div>

          <button class="mobile-discover-sheet__more-btn" @click="loadSuggestedUsers">
            {{ isLoadingSuggestions ? "Loading..." : "Show more" }}
          </button>
        </section>
      </div>
    </v-card>
  </v-bottom-sheet>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuth } from "@/composables/useAuth";
import { useSdk } from "@/composables/useSdk";
import type { TrendingMusicUser, SuggestedUser as SdkSuggestedUser } from "@cc-consulting-nv/ccsdk";

// Types
interface TrendingHashtag {
  hashtag: string;
  count: number;
}

interface TrendingSong {
  ulid: string;
  title: string;
  artist: string;
  username: string;
  coverImage?: string;
}

interface SuggestedUser extends Omit<SdkSuggestedUser, 'avatar' | 'ProfileEngagement'> {
  avatar: string;
  ProfileEngagement?: SdkSuggestedUser['ProfileEngagement'];
  isFollowing: boolean;
  isFollowLoading: boolean;
}

// Props/Emits
const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
}>();

// Composables
const router = useRouter();
const { isAuthenticated } = useAuth();
const { sdk, isInitialized } = useSdk();

// State
const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const sheetHeight = computed(() => {
  const viewportHeight = window.innerHeight;
  return `${Math.min(viewportHeight * 0.85, 600)}px`;
});

// Trending data
const trendingHashtags = ref<TrendingHashtag[]>([]);
const trendingSongs = ref<TrendingSong[]>([]);
const trendingCreators = ref<TrendingMusicUser[]>([]);
const isLoadingTrending = ref(false);

const hasTrendingContent = computed(() => {
  return (
    trendingHashtags.value.length > 0 ||
    trendingSongs.value.length > 0 ||
    trendingCreators.value.length > 0
  );
});

// Suggested users
const suggestedUsers = ref<SuggestedUser[]>([]);
const isLoadingSuggestions = ref(false);

// Methods
function close() {
  isOpen.value = false;
}

function formatCount(count: number | undefined): string {
  if (count === undefined || count === null) return '0';
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  return count.toString();
}

async function loadTrendingData() {
  if (!isAuthenticated.value) return;
  if (!isInitialized.value || !sdk) return;

  isLoadingTrending.value = true;
  try {
    const [hashtags, songs, creators] = await Promise.all([
      sdk.getTrendingHashtags(5).catch(() => []),
      sdk.getTrendingSongs(5).catch(() => []),
      sdk.getTrendingMusicUsers({ limit: 5 }).catch(() => []),
    ]);

    trendingHashtags.value = hashtags;
    trendingSongs.value = songs;
    trendingCreators.value = creators;
  } catch (err) {
    console.error("Failed to load trending data:", err);
  } finally {
    isLoadingTrending.value = false;
  }
}

async function loadSuggestedUsers() {
  if (!isAuthenticated.value || !isInitialized.value || !sdk) return;

  isLoadingSuggestions.value = true;
  try {
    const users = await sdk.getUserSuggestions(5);
    suggestedUsers.value = (users || [])
      .filter((u) => !!u.ulid && !!u.username)
      .map((u): SuggestedUser => ({
        ulid: u.ulid,
        name: u.name,
        username: u.username,
        avatar: u.avatar || "",
        bio: u.bio,
        followerCount: u.followerCount || 0,
        ProfileEngagement: u.ProfileEngagement,
        isFollowing: u.ProfileEngagement?.isFollowing ?? false,
        isFollowLoading: false,
      }));
  } catch (err) {
    console.error("Failed to load suggested users:", err);
    suggestedUsers.value = [];
  } finally {
    isLoadingSuggestions.value = false;
  }
}

// Follow state helpers
function getFollowButtonClass(user: SuggestedUser): Record<string, boolean> {
  const isFollowing = user.isFollowing;
  const isFollowingYou = user.ProfileEngagement?.isFollowingYou ?? false;
  return {
    'mobile-discover-sheet__follow-btn--following': isFollowing,
    'mobile-discover-sheet__follow-btn--follows-you': !isFollowing && isFollowingYou,
  };
}

function getFollowIcon(user: SuggestedUser): string {
  if (user.isFollowing) return 'mdi-account-check';
  if (user.ProfileEngagement?.isFollowingYou) return 'mdi-account-arrow-left';
  return 'mdi-account-plus-outline';
}

function getFollowButtonText(user: SuggestedUser): string {
  if (user.isFollowing) return 'Following';
  if (user.ProfileEngagement?.isFollowingYou) return 'Follow Back';
  return 'Follow';
}

async function toggleFollow(user: SuggestedUser) {
  if (user.isFollowLoading) return;

  user.isFollowLoading = true;
  try {
    if (user.isFollowing) {
      if (user.username) {
        await sdk.unfollowUser(user.username);
      } else {
        await sdk.unfollowUserByUlid(user.ulid);
      }
      user.isFollowing = false;
    } else {
      if (user.username) {
        await sdk.followUser(user.username);
      } else {
        await sdk.followUserByUlid(user.ulid);
      }
      user.isFollowing = true;
    }
  } catch (err) {
    console.error("Failed to toggle follow:", err);
  } finally {
    user.isFollowLoading = false;
  }
}

// Navigation
function navigateToHashtag(tag: string) {
  const hashtagQuery = tag.startsWith('#') ? tag : `#${tag}`;
  router.push({ path: "/search", query: { q: hashtagQuery } });
  close();
}

function navigateToSong(ulid: string) {
  router.push(`/song/${ulid}`);
  close();
}

function navigateToCreator(username: string) {
  router.push(`/profile/${username}`);
  close();
}

function navigateToProfile(user: SuggestedUser) {
  router.push(`/profile/${user.username}`);
  close();
}

function goToExplore() {
  router.push('/explore');
  close();
}

// Load data when sheet opens
watch(isOpen, (open) => {
  if (open) {
    loadTrendingData();
    if (isAuthenticated.value) {
      loadSuggestedUsers();
    }
  }
});

// Initial load
onMounted(() => {
  if (isOpen.value) {
    loadTrendingData();
    if (isAuthenticated.value) {
      loadSuggestedUsers();
    }
  }
});
</script>

<style scoped lang="scss">
.mobile-discover-sheet {
  // Ensure bottom sheet appears above all page content including fixed headers
  // App header is z-index: 3002, sidebar is 3004, modal overlay is 4000
  :deep(.v-overlay__scrim) {
    z-index: 4001;
  }

  :deep(.v-overlay__content) {
    z-index: 4002;
  }

  :deep(.v-bottom-sheet__content) {
    border-radius: 24px 24px 0 0;
    overflow: hidden;
  }

  &__card {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: rgb(var(--v-theme-surface));
    border-radius: 24px 24px 0 0;
  }

  &__handle {
    display: flex;
    justify-content: center;
    padding: 12px 0 8px;
  }

  &__handle-bar {
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background: rgba(var(--v-theme-on-surface), 0.2);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px 16px;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 10px;

    h2 {
      font-family: var(--font-display);
      font-size: 1.25rem;
      font-weight: 600;
      margin: 0;
      color: rgb(var(--v-theme-on-surface));
    }
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    background: rgba(var(--v-theme-on-surface), 0.05);
    border-radius: 50%;
    color: rgba(var(--v-theme-on-surface), 0.6);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.1);
      color: rgb(var(--v-theme-on-surface));
    }
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    padding-bottom: calc(16px + var(--safe-area-bottom));
    -webkit-overflow-scrolling: touch;
  }

  &__section {
    margin-bottom: 8px;
  }

  &__section-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;

    h3 {
      font-family: var(--font-display);
      font-size: 1rem;
      font-weight: 600;
      margin: 0;
      color: rgb(var(--v-theme-on-surface));
    }
  }

  &__loading,
  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;

    p {
      font-size: 0.875rem;
      color: rgba(var(--v-theme-on-surface), 0.5);
      margin: 0;
    }
  }

  &__trends {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__trend-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__trend-label {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 0;
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(var(--v-theme-on-surface), 0.5);
  }

  &__trend-item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px;
    border: none;
    background: rgba(var(--v-theme-on-surface), 0.03);
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: left;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.06);
    }

    &:active {
      transform: scale(0.98);
    }
  }

  &__trend-cover {
    width: 44px;
    height: 44px;
    border-radius: 8px;
    overflow: hidden;
    background: rgba(var(--v-theme-primary), 0.1);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    .v-icon {
      color: rgb(var(--v-theme-primary));
    }
  }

  &__trend-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    overflow: hidden;
    background: linear-gradient(135deg, rgb(var(--v-theme-primary)), rgb(var(--v-theme-secondary)));
    padding: 2px;
    flex-shrink: 0;

    .v-img,
    span {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background: rgb(var(--v-theme-surface-variant));
      font-size: 0.75rem;
      font-weight: 600;
      color: rgb(var(--v-theme-on-surface));
    }
  }

  &__trend-content {
    flex: 1;
    min-width: 0;
  }

  &__trend-name {
    display: block;
    font-weight: 600;
    font-size: 0.9375rem;
    color: rgb(var(--v-theme-on-surface));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__trend-count {
    display: block;
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
    margin-top: 2px;
  }

  &__trend-arrow {
    color: rgba(var(--v-theme-on-surface), 0.3);
    flex-shrink: 0;
  }

  &__more-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    padding: 14px;
    margin-top: 12px;
    border: none;
    background: rgba(var(--v-theme-primary), 0.08);
    border-radius: 12px;
    font-family: var(--font-body);
    font-size: 0.875rem;
    font-weight: 600;
    color: rgb(var(--v-theme-primary));
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(var(--v-theme-primary), 0.12);
    }

    &:active {
      transform: scale(0.98);
    }
  }

  // Suggestions
  &__suggestions {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__suggestion {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: rgba(var(--v-theme-on-surface), 0.03);
    border-radius: 12px;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.06);
    }
  }

  &__suggestion-avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    overflow: hidden;
    background: linear-gradient(135deg, rgb(var(--v-theme-primary)), rgb(var(--v-theme-secondary)));
    padding: 2px;
    flex-shrink: 0;

    .v-img,
    span {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background: rgb(var(--v-theme-surface-variant));
      font-size: 1rem;
      font-weight: 600;
      color: rgb(var(--v-theme-on-surface));
    }
  }

  &__suggestion-info {
    flex: 1;
    min-width: 0;
  }

  &__suggestion-name {
    display: block;
    font-weight: 600;
    font-size: 0.9375rem;
    color: rgb(var(--v-theme-on-surface));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__suggestion-handle {
    display: block;
    font-size: 0.8125rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
  }

  &__follow-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-width: 90px;
    padding: 8px 14px;
    border: 1px solid rgba(var(--v-theme-on-surface), 0.15);
    background: rgba(var(--v-theme-on-surface), 0.03);
    border-radius: 50px;
    font-family: var(--font-body);
    font-size: 0.75rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
      background: rgb(var(--v-theme-primary));
      border-color: rgb(var(--v-theme-primary));
      color: rgb(var(--v-theme-on-primary));
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    &--following {
      background: transparent;
      border-color: rgba(var(--v-theme-primary), 0.5);
      color: rgb(var(--v-theme-primary));
    }

    &--follows-you {
      background: rgba(var(--v-theme-primary), 0.1);
      border-color: rgba(var(--v-theme-primary), 0.3);
      color: rgb(var(--v-theme-primary));
    }
  }
}

// Light theme
.v-theme--light {
  .mobile-discover-sheet {
    &__trend-item {
      background: rgba(0, 0, 0, 0.02);

      &:hover {
        background: rgba(0, 0, 0, 0.04);
      }
    }

    &__suggestion {
      background: rgba(0, 0, 0, 0.02);

      &:hover {
        background: rgba(0, 0, 0, 0.04);
      }
    }

    &__follow-btn {
      background: rgba(0, 0, 0, 0.02);
      border-color: rgba(0, 0, 0, 0.1);
    }
  }
}
</style>
